import React, { useEffect, useState, useCallback, useRef } from "react"
import { Download, X } from "lucide-react"
import toast from "react-hot-toast"
import GoalPlanningForm, {
  EMPTY_GOAL_PLANNING_FORM,
  mapGoalPlanningToForm,
} from "../../Components/admin/GoalPlanningForm"
import {
  buildFormPdfFilename,
  downloadFormAsPdf,
} from "../../utils/downloadFormPdf"
import FormHeaderLogo from "../../Components/admin/FormHeaderLogo"

const GoalPlanningModal = ({
  isOpen,
  onClose,
  mode = "initiate",
  adminName,
  goalPlanning,
  onInitiate,
  onAdminDraftSave,
  onAdminSubmit,
  onConferenceSubmit,
  onReinitiate,
  loading = false,
}) => {
  const [formData, setFormData] = useState(EMPTY_GOAL_PLANNING_FORM)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const initializedRef = useRef(false)
  const formContentRef = useRef(null)

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false
      return
    }

    if (!initializedRef.current) {
      if (goalPlanning) {
        setFormData(mapGoalPlanningToForm(goalPlanning))
      } else {
        setFormData(EMPTY_GOAL_PLANNING_FORM)
      }
      initializedRef.current = true
    }
  }, [isOpen, goalPlanning])

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleSaveDraft = () => {
    if (mode === "admin") {
      onAdminDraftSave?.(formData)
    }
  }

  const handleSubmit = () => {
    if (mode === "initiate") {
      onInitiate?.()
      return
    }

    if (mode === "admin") {
      onAdminSubmit?.(formData)
      return
    }

    if (mode === "super_admin_conference") {
      onConferenceSubmit?.({
        conferenceNotes: formData.conferenceNotes,
        tier2Support: formData.tier2Support,
        conferenceDate: formData.conferenceDate || null,
      })
    }
  }

  const handleDownloadPdf = async () => {
    if (!formContentRef.current) return

    try {
      setDownloadingPdf(true)
      const filename = buildFormPdfFilename({
        title: "Goal-Planning",
        adminName,
        year: goalPlanning?.year,
      })
      await downloadFormAsPdf(formContentRef.current, filename)
      toast.success("PDF downloaded")
    } catch (error) {
      toast.error(error?.message || "Failed to download PDF")
    } finally {
      setDownloadingPdf(false)
    }
  }

  const getSubmitLabel = () => {
    if (mode === "initiate") return "Initiate Goal Planning"
    if (mode === "admin") return "Submit to Super Admin"
    if (mode === "super_admin_conference") return "Complete Goal Setting Conference"
    return "Close"
  }

  const canDownloadPdf =
    mode === "view" && goalPlanning?.status === "completed"

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[95vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-[#241763]">Goal Planning</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Close goal planning modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div ref={formContentRef}>
            <FormHeaderLogo />

            {mode === "initiate" && (
              <p className="text-sm text-gray-600 my-4">
                Initiate this goal planning form for{" "}
                <span className="font-semibold">{adminName}</span>. The admin will
                receive it on their account settings dashboard to complete.
              </p>
            )}

            {mode === "admin" && (
              <>
                {goalPlanning?.reinitiatedAt && (
                  <div
                    className="my-4 rounded border border-[#ED7D31] bg-[#FDE9D9] px-3 py-2 text-sm text-[#833C0C]"
                    role="status"
                  >
                    This form was{" "}
                    <span className="font-semibold">reinitiated</span> by Super
                    Admin. Please review your answers, update if needed, and
                    resubmit.
                  </div>
                )}
                <p className="text-sm text-gray-600 my-4">
                  Fill in any fields and click Save Draft to keep your progress.
                  When everything is ready, submit to Super Admin. The Goal
                  Setting Conference section will be completed by the Super
                  Admin.
                </p>
              </>
            )}

            {mode === "super_admin_conference" && (
              <p className="text-sm text-gray-600 my-4">
                Review the admin&apos;s responses and complete the Goal Setting
                Conference section below.
              </p>
            )}

            <GoalPlanningForm
              formData={formData}
              onChange={handleChange}
              mode={mode}
              adminName={adminName}
              record={goalPlanning}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            {mode === "view" ? "Close" : "Cancel"}
          </button>

          {canDownloadPdf && (
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="px-4 py-2 text-sm border border-[#241763] text-[#241763] rounded hover:bg-[#241763]/5 disabled:opacity-50 flex items-center gap-2"
              aria-label="Download form as PDF"
            >
              <Download className="w-4 h-4" />
              {downloadingPdf ? "Preparing PDF..." : "Download PDF"}
            </button>
          )}

          {mode === "admin" && (
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={loading}
              className="px-4 py-2 text-sm border border-[#241763] text-[#241763] rounded hover:bg-[#241763]/5 disabled:opacity-50"
              aria-label="Save draft"
            >
              {loading ? "Please wait..." : "Save Draft"}
            </button>
          )}

          {mode === "super_admin_conference" && onReinitiate && (
            <button
              type="button"
              onClick={() => {
                onReinitiate()
                onClose()
              }}
              disabled={loading}
              className="px-4 py-2 text-sm bg-[#ED7D31] text-white rounded hover:opacity-90 disabled:opacity-50"
              aria-label="Reinitiate goal planning"
            >
              {loading ? "Please wait..." : "Reinitiate"}
            </button>
          )}

          {mode !== "view" && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 text-sm bg-[#241763] text-white rounded hover:bg-[#1a1047] disabled:opacity-50"
            >
              {loading ? "Please wait..." : getSubmitLabel()}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default GoalPlanningModal
